import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Plus, Truck, AlertTriangle, CheckCircle2, XCircle, Search, Edit2, Ban,
  PackageOpen, ChevronUp, ChevronDown, RefreshCw, ExternalLink, DollarSign, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import RestockDialog from '../components/RestockDialog';
import ExpectedOrderDialog from '../components/ExpectedOrderDialog';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending', color: 'text-amber-600' },
  { key: 'overdue', label: 'Overdue', color: 'text-red-600' },
  { key: 'received', label: 'Received', color: 'text-emerald-600' },
  { key: 'cancelled', label: 'Cancelled', color: 'text-slate-500' },
  { key: 'all', label: 'All', color: 'text-slate-700' },
];

const formatDate = (s) => {
  if (!s) return '—';
  try {
    return new Date(String(s).split('T')[0]).toLocaleDateString();
  } catch { return s; }
};

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const OpenOrdersPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [tab, setTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortKey, setSortKey] = useState('expected_arrival_date');
  const [sortDir, setSortDir] = useState('asc');

  // Dialogs
  const [restockTarget, setRestockTarget] = useState(null); // { item, eo }
  const [editing, setEditing] = useState(null); // EO being edited
  const [editForm, setEditForm] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [flagOpen, setFlagOpen] = useState(false);

  // iter69 — received-match badge: derive sum of qty received per linked EO from transactions
  const [matchedByEoId, setMatchedByEoId] = useState({}); // {eo_id: {received_qty, txn_count, last_at}}

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ordersRes, summaryRes, vRes, iRes, txRes] = await Promise.all([
        api().get('/expected-orders'),
        api().get('/expected-orders/summary').catch(() => ({ data: null })),
        api().get('/vendors').catch(() => ({ data: [] })),
        api().get('/items').catch(() => ({ data: [] })),
        api().get('/transactions?limit=2000').catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data || []);
      setSummary(summaryRes.data);
      setVendors(vRes.data || []);
      setItems(iRes.data || []);
      // Aggregate transactions where reference_kind=='open_order' or reference_doc_number matches PO#
      const idx = {};
      (txRes.data || []).forEach(t => {
        if (!['Receive', 'Return'].includes(t.transaction_type)) return;
        let key = null;
        if (t.reference_kind === 'open_order' && t.reference_doc_id) key = t.reference_doc_id;
        // Also bind by PO number for back-compat with legacy data
        if (!key && t.reference_doc_number) key = t.reference_doc_number;
        if (!key) return;
        const e = idx[key] || { received_qty: 0, txn_count: 0, last_at: null };
        e.received_qty += Number(t.quantity || 0);
        e.txn_count += 1;
        const ts = t.timestamp || t.transaction_date;
        if (!e.last_at || (ts && ts > e.last_at)) e.last_at = ts;
        idx[key] = e;
      });
      // Also index by purchase_order_number for easy lookup
      (ordersRes.data || []).forEach(o => {
        if (o.purchase_order_number && idx[o.purchase_order_number] && !idx[o.id]) {
          idx[o.id] = idx[o.purchase_order_number];
        }
      });
      setMatchedByEoId(idx);
    } catch (e) {
      toast.error('Failed to load open orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let rows = [...orders];
    // Tab
    if (tab === 'overdue') {
      rows = rows.filter(r => r.status === 'pending' && r.is_overdue);
    } else if (tab === 'all') {
      // no-op
    } else {
      rows = rows.filter(r => r.status === tab);
    }
    if (vendorFilter && vendorFilter !== 'all') {
      rows = rows.filter(r => r.vendor_id === vendorFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.purchase_order_number || '').toLowerCase().includes(q) ||
        (r.item_sku || '').toLowerCase().includes(q) ||
        (r.item_name || '').toLowerCase().includes(q) ||
        (r.vendor_name || '').toLowerCase().includes(q)
      );
    }
    if (startDate) {
      rows = rows.filter(r => !r.expected_arrival_date || r.expected_arrival_date >= startDate);
    }
    if (endDate) {
      rows = rows.filter(r => !r.expected_arrival_date || r.expected_arrival_date <= endDate);
    }
    rows.sort((a, b) => {
      const va = a[sortKey] ?? '';
      const vb = b[sortKey] ?? '';
      const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [orders, tab, search, vendorFilter, startDate, endDate, sortKey, sortDir]);

  const counts = summary?.counts || {};

  const tabCount = (k) => {
    if (k === 'overdue') return counts.overdue || 0;
    if (k === 'all') return counts.total || 0;
    return counts[k] || 0;
  };

  const openReceive = (eo) => {
    const item = items.find(i => i.id === eo.item_id);
    if (!item) {
      toast.error('Item not found — refresh and try again');
      return;
    }
    setRestockTarget({ item, eo });
  };

  const openEdit = (eo) => {
    setEditing(eo);
    setEditForm({
      expected_quantity: eo.expected_quantity ?? '',
      vendor_id: eo.vendor_id || '',
      purchase_order_number: eo.purchase_order_number || '',
      expected_arrival_date: eo.expected_arrival_date || '',
      notes: eo.notes || '',
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api().put(`/expected-orders/${editing.id}`, {
        ...editForm,
        expected_quantity: parseFloat(editForm.expected_quantity) || 0,
      });
      toast.success('Open order updated');
      setEditing(null); setEditForm(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    try {
      await api().post(`/expected-orders/${cancelTarget.id}/cancel`, { reason: cancelReason });
      toast.success('Order cancelled');
      setCancelTarget(null); setCancelReason('');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel');
    }
  };

  const SortIcon = ({ keyName }) => sortKey !== keyName ? null :
    sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />;

  return (
    <div className="space-y-6" data-testid="open-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Truck className="w-6 h-6 text-orange-600" /> Open Orders
          </h1>
          <p className="text-slate-500 text-sm">Track items on order. One-click receive when shipments arrive.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchAll}
            disabled={loading}
            data-testid="open-orders-refresh-btn"
            className="rounded-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button
            onClick={() => setFlagOpen(true)}
            data-testid="open-orders-flag-btn"
            className="bg-orange-600 hover:bg-orange-700 rounded-sm"
          >
            <Plus className="w-4 h-4 mr-2" /> Flag Order
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="industrial-card" data-testid="kpi-pending">
          <CardContent className="p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500">Pending</div>
            <div className="text-2xl font-semibold mt-1">{counts.pending || 0}</div>
          </CardContent>
        </Card>
        <Card className="industrial-card" data-testid="kpi-overdue">
          <CardContent className="p-3">
            <div className="text-xs uppercase tracking-wider text-red-600">Overdue</div>
            <div className="text-2xl font-semibold mt-1 text-red-600">{counts.overdue || 0}</div>
          </CardContent>
        </Card>
        <Card className="industrial-card" data-testid="kpi-pending-value">
          <CardContent className="p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Pending $</div>
            <div className="text-2xl font-semibold mt-1">{fmt$(summary?.pending_value_usd || 0)}</div>
          </CardContent>
        </Card>
        <Card className="industrial-card" data-testid="kpi-avg-days">
          <CardContent className="p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500">Avg Days to ETA</div>
            <div className="text-2xl font-semibold mt-1">
              {summary?.avg_days_to_arrival !== null && summary?.avg_days_to_arrival !== undefined
                ? `${Math.round(summary.avg_days_to_arrival)}d`
                : '—'}
            </div>
          </CardContent>
        </Card>
        <Card className="industrial-card" data-testid="kpi-top-vendors">
          <CardContent className="p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Building2 className="w-3 h-3" /> Top Vendors (open $)</div>
            <div className="mt-1 space-y-0.5">
              {(summary?.top_vendors || []).length === 0 && (
                <span className="text-xs text-slate-400 italic">—</span>
              )}
              {(summary?.top_vendors || []).map(v => (
                <div key={v.vendor_id} className="flex justify-between text-xs" data-testid={`top-vendor-${v.vendor_id}`}>
                  <span className="truncate max-w-[120px]">{v.vendor_name}</span>
                  <span className="mono text-slate-700">{fmt$(v.open_value_usd)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & tabs */}
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="open-orders-tabs">
              {STATUS_TABS.map(t => (
                <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>
                  <span className={t.color}>{t.label}</span>
                  <span className="ml-2 text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{tabCount(t.key)}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                data-testid="open-orders-search"
                placeholder="Search PO #, SKU, item, vendor…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rounded-sm h-9 pl-8 text-sm"
              />
            </div>
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger data-testid="open-orders-vendor-filter" className="rounded-sm h-9 text-sm">
                <SelectValue placeholder="All vendors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vendors</SelectItem>
                {vendors.filter(v => !v.archived).map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              data-testid="open-orders-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              placeholder="ETA from"
              className="rounded-sm h-9 text-sm"
            />
            <Input
              data-testid="open-orders-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="ETA to"
              className="rounded-sm h-9 text-sm"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs cursor-pointer" onClick={() => handleSort('purchase_order_number')}>PO # <SortIcon keyName="purchase_order_number" /></TableHead>
                <TableHead className="text-xs cursor-pointer" onClick={() => handleSort('item_sku')}>Item <SortIcon keyName="item_sku" /></TableHead>
                <TableHead className="text-xs cursor-pointer" onClick={() => handleSort('vendor_name')}>Vendor <SortIcon keyName="vendor_name" /></TableHead>
                <TableHead className="text-xs cursor-pointer" onClick={() => handleSort('expected_quantity')}>Qty <SortIcon keyName="expected_quantity" /></TableHead>
                <TableHead className="text-xs cursor-pointer" onClick={() => handleSort('expected_arrival_date')}>ETA <SortIcon keyName="expected_arrival_date" /></TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs">Created By</TableHead>
                <TableHead className="text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-slate-400 italic" data-testid="open-orders-empty">
                    <PackageOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No orders match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map(eo => {
                const isPending = eo.status === 'pending';
                return (
                  <TableRow key={eo.id} data-testid={`open-order-row-${eo.id}`}>
                    <TableCell className="text-xs font-mono">{eo.purchase_order_number || '—'}</TableCell>
                    <TableCell className="text-xs">
                      <button
                        type="button"
                        onClick={() => navigate(`/items/view/${eo.item_id}`)}
                        className="font-medium text-slate-900 hover:text-orange-600 hover:underline text-left flex items-center gap-1"
                        data-testid={`open-order-item-${eo.id}`}
                      >
                        {eo.item_sku} <ExternalLink className="w-3 h-3 opacity-50" />
                      </button>
                      <div className="text-[11px] text-slate-500 truncate max-w-[220px]">{eo.item_name}</div>
                      <div className="text-[11px] text-slate-500">On hand: <span className="mono">{eo.item_on_hand ?? 0} {eo.item_uom}</span></div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-700">{eo.vendor_name || <span className="italic text-slate-400">unassigned</span>}</TableCell>
                    <TableCell className="text-xs mono">
                      <div>{eo.expected_quantity} {eo.item_uom}</div>
                      {(() => {
                        const m = matchedByEoId[eo.id] || matchedByEoId[eo.purchase_order_number];
                        if (!m || m.received_qty <= 0) return null;
                        const expected = Number(eo.expected_quantity || 0);
                        const pct = expected ? Math.min(100, (m.received_qty / expected) * 100) : 0;
                        const full = m.received_qty >= expected && expected > 0;
                        return (
                          <div className="mt-1" data-testid={`oo-match-${eo.id}`} title={`${m.txn_count} transaction(s) matched · last ${m.last_at ? new Date(m.last_at).toLocaleString() : ''}`}>
                            <div className="flex items-center gap-1">
                              {full && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                              <span className={`text-[10px] ${full ? 'text-emerald-700 font-semibold' : 'text-blue-700'}`}>
                                {m.received_qty} matched · {pct.toFixed(0)}%
                              </span>
                            </div>
                            <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden mt-0.5">
                              <div className={`h-full ${full ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>{formatDate(eo.expected_arrival_date)}</div>
                      {isPending && eo.days_until_arrival !== null && eo.days_until_arrival !== undefined && (
                        <div className={`text-[10px] mt-0.5 ${eo.is_overdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                          {eo.is_overdue ? `${Math.abs(eo.days_until_arrival)}d overdue` : `${eo.days_until_arrival}d to ETA`}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {eo.status === 'pending' && eo.is_overdue && (
                        <span className="inline-block px-1.5 py-0.5 rounded-sm uppercase tracking-wide text-[10px] bg-red-100 text-red-700">Overdue</span>
                      )}
                      {eo.status === 'pending' && !eo.is_overdue && (
                        <span className="inline-block px-1.5 py-0.5 rounded-sm uppercase tracking-wide text-[10px] bg-amber-100 text-amber-700">Pending</span>
                      )}
                      {eo.status === 'received' && (
                        <span className="inline-block px-1.5 py-0.5 rounded-sm uppercase tracking-wide text-[10px] bg-emerald-100 text-emerald-700">Received</span>
                      )}
                      {eo.status === 'cancelled' && (
                        <span className="inline-block px-1.5 py-0.5 rounded-sm uppercase tracking-wide text-[10px] bg-slate-100 text-slate-600">Cancelled</span>
                      )}
                    </TableCell>
                    <TableCell className="text-[11px] text-slate-500">{eo.created_by_name || '—'}<br/><span className="text-[10px]">{formatDate(eo.created_at)}</span></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {isPending && (
                        <Button
                          size="sm"
                          onClick={() => openReceive(eo)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-7 text-xs"
                          data-testid={`open-order-receive-${eo.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Receive Now
                        </Button>
                      )}
                      {isPending && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEdit(eo)}
                          className="rounded-sm h-7 text-xs"
                          data-testid={`open-order-edit-${eo.id}`}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {isPending && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setCancelTarget(eo)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm h-7 text-xs"
                          data-testid={`open-order-cancel-${eo.id}`}
                        >
                          <Ban className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {eo.status === 'received' && eo.restock_session_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/items/view/${eo.item_id}`)}
                          className="rounded-sm h-7 text-xs text-emerald-700"
                          data-testid={`open-order-view-${eo.id}`}
                        >
                          View Receipt
                        </Button>
                      )}
                      {eo.status === 'cancelled' && eo.cancel_reason && (
                        <span className="text-[10px] text-slate-500 italic" title={eo.cancel_reason}>{eo.cancel_reason.slice(0, 40)}</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Receive Now: pre-fill RestockDialog with the EO */}
      {restockTarget && (
        <RestockDialog
          open={!!restockTarget}
          onOpenChange={(o) => !o && setRestockTarget(null)}
          item={restockTarget.item}
          prefillExpectedOrder={restockTarget.eo}
          onDone={() => { setRestockTarget(null); fetchAll(); }}
        />
      )}

      {/* Flag Order */}
      <ExpectedOrderDialog
        open={flagOpen}
        onOpenChange={setFlagOpen}
        item={null}
        onCreated={() => { setFlagOpen(false); fetchAll(); }}
      />

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="open-order-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Open Order</DialogTitle>
            <DialogDescription>{editing?.item_sku} — {editing?.item_name}</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Expected Qty</Label>
                  <Input
                    data-testid="edit-eo-qty"
                    type="number"
                    step="any"
                    value={editForm.expected_quantity}
                    onChange={(e) => setEditForm({ ...editForm, expected_quantity: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">PO Number</Label>
                  <Input
                    data-testid="edit-eo-po"
                    value={editForm.purchase_order_number}
                    onChange={(e) => setEditForm({ ...editForm, purchase_order_number: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Vendor</Label>
                  <Select
                    value={editForm.vendor_id || 'none'}
                    onValueChange={(v) => setEditForm({ ...editForm, vendor_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="edit-eo-vendor" className="rounded-sm">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {vendors.filter(v => !v.archived).map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Expected Arrival</Label>
                  <Input
                    data-testid="edit-eo-eta"
                    type="date"
                    value={editForm.expected_arrival_date || ''}
                    onChange={(e) => setEditForm({ ...editForm, expected_arrival_date: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                <Textarea
                  data-testid="edit-eo-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  className="rounded-sm"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="edit-eo-cancel">Cancel</Button>
            <Button onClick={saveEdit} className="bg-slate-900 hover:bg-slate-800" data-testid="edit-eo-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent data-testid="cancel-eo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" /> Cancel Open Order
            </DialogTitle>
            <DialogDescription>
              {cancelTarget?.purchase_order_number} · {cancelTarget?.item_sku} · {cancelTarget?.expected_quantity} {cancelTarget?.item_uom || 'ea'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Reason (recorded on the order)</Label>
            <Textarea
              data-testid="cancel-eo-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              className="rounded-sm"
              placeholder="e.g. vendor cancelled · duplicate PO · item discontinued"
            />
            <p className="text-[11px] text-slate-500">
              The order will be moved to the Cancelled tab. This is reversible only via re-flagging a new order.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)} data-testid="cancel-eo-cancel">Keep Order</Button>
            <Button onClick={confirmCancel} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-eo-confirm">
              <Ban className="w-4 h-4 mr-2" /> Cancel Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpenOrdersPage;
